import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

export type BreadcrumbItem = {
  label: string;
  /** Relative; the last crumb has none and renders as text. */
  href?: string;
};

type Props = {
  items: BreadcrumbItem[];
  className?: string;
};

/**
 * Carries schema.org microdata, which is the part that is easy to drop in a port and
 * expensive to notice missing — Google reads it for the breadcrumb rich result. The
 * `position` values are 1-based and assigned here rather than by the caller so they
 * cannot disagree with the order rendered.
 *
 * Microdata rather than a JSON-LD block, and deliberately so: Google treats JSON-LD,
 * microdata and RDFa as equally valid ("all 3 formats are equally fine for Google"), and
 * a breadcrumb trail is one of the few cases where the markup is genuinely free — the
 * data is the anchors already on the page, so there is no second copy to keep in step.
 * It also matches every other breadcrumb in this repo, legacy templates included.
 *
 * The spacing under the trail lives here rather than at the call sites, so that every
 * page's content sits the same distance below it. There is deliberately none above it:
 * the distance from the header is the layout's standard padding, which every page gets
 * whether or not it draws a trail. A margin here too would mean a trail page sat lower
 * than a trail-less one, and — because this is hidden on the phone, below — that the
 * same page sat differently at two widths. That is what the `pt-5 md:pt-0` at four call
 * sites was compensating for; it is gone.
 *
 * Off the phone, and here rather than at the call sites: the legacy stylesheet hides
 * every trail on the site below 576px, and a trail that each page decides about
 * separately is how half of them ended up visible there.
 */
export default function Breadcrumbs({ items, className }: Props) {
  const { buildUrl } = useUrlGenerator();

  return (
    <ol
      itemScope
      itemType="http://schema.org/BreadcrumbList"
      className={cn('hidden md:flex flex-wrap items-center gap-1 list-none p-0 mt-0 mb-5 text-sm', className)}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <li
            key={`${item.label}-${index}`}
            itemProp="itemListElement"
            itemScope
            itemType="http://schema.org/ListItem"
            className="flex items-center"
          >
            <meta itemProp="position" content={String(index + 1)} />

            {item.href && !isLast ? (
              <a href={buildUrl(item.href)} itemProp="item" className="text-slate-600 hover:underline">
                <span itemProp="name">{item.label}</span>
              </a>
            ) : (
              // No `item` here on purpose. Google: "If the breadcrumb is the last item in
              // the breadcrumb trail, item is not required" — it falls back to the page's
              // own url. Naming an empty Thing instead, as this used to, hands validators
              // an item with no url and no name, which is worse than leaving it out.
              <span itemProp="name" className="text-slate-900 font-medium">
                {item.label}
              </span>
            )}

            {!isLast && (
              <FontAwesomeIcon icon={faAngleRight} className="text-slate-400 px-2 text-xs" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
